import * as React from 'react';

import { Button, cn, Col, Loading, Row } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideX,
  LucideSearch,
  LucidePanelLeftClose,
  LucideSquarePen,
} from 'lucide-react';

import { presetFilters } from '../_state/_types';
import * as conversationListState from '../_state/conversationList.state';
import { useTranslations } from '../i18n/useTranslations';

import { ConversationCard } from './ConversationCard';

type ChatSidebarProps = {
  onClose?: () => void;
};

export const ConversationListPanel = ({ onClose }: ChatSidebarProps) => {
  const t = useTranslations();

  const [isChatListHidden, setIsChatListHidden] = React.useState(false);

  const isLoadingConversations = useAtomValue(
    conversationListState.$isLoadingConversations,
  );

  const [searchTerm, setSearchTerm] = useAtom(
    conversationListState.$searchTerm.value,
  );

  const [presetFilter, setPresetFilter] = useAtom(
    conversationListState.$presetFilter,
  );

  const filteredConversations = useAtomValue(
    conversationListState.$filteredConversations,
  );

  const createConversation = useSetAtom(
    conversationListState.$createConversation,
  );

  const handleCreateConversation = () => {
    // setIsCreatingConversation(true);
    // setSelectedConversation(undefined);
    createConversation();
  };

  return (
    <div
      className={cn(
        '@ConversationListPanel',
        'relative flex flex-col gap-2.5 overflow-clip rounded-[10px] border-neutral/70 bg-neutral p-2.5',
        !isChatListHidden && 'w-[280px]',
      )}
    >
      {/* 🟠🟠🟠 HEADER BUTTONS */}
      <div
        className={cn(
          'flex items-center justify-between gap-2',
          isChatListHidden && 'flex-col items-start',
        )}
      >
        <Button
          variant="subtle"
          color="white"
          size="icon"
          className={'size-input shrink-0'}
          icon={<LucideX />}
          onClick={onClose}
        />
        <Button
          onClick={handleCreateConversation}
          icon={<LucideSquarePen />}
          className={cn(isChatListHidden && 'size-input')}
        >
          {isChatListHidden ? undefined : t.conversationList.newConversation}
        </Button>
      </div>

      {!isChatListHidden && (
        <Col className="h-full gap-2">
          <Col className={'gap-2.5 **:shrink-0'}>
            {/* 🟠🟠🟠 SEARCH BAR */}
            <Row
              gap2
              className={cn(
                'relative rounded border border-white/20 bg-white/10 px-2.5 py-2 outline-3',
                'focus-within:border-primary/80 focus-within:outline-primary/30',
              )}
            >
              <LucideSearch className={'size-[16px] text-white opacity-40'} />
              <input
                type="text"
                placeholder={t.conversationList.search}
                value={searchTerm}
                className={
                  'w-full border-none text-sm text-white outline-none placeholder:text-white/30'
                }
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Row>

            {/* 🟠🟠🟠 FILTERS */}
            <div className="flex gap-1 **:shrink-0">
              {presetFilters.map((filter) => (
                <button
                  key={filter}
                  className={cn(
                    'grow cursor-pointer rounded border border-white/20 bg-white/10 px-2 py-0.5 text-sm text-white/60 capitalize',
                    'hover:border-white/30 hover:bg-white/20',
                    presetFilter === filter && 'bg-white! text-neutral',
                  )}
                  onClick={() => setPresetFilter(filter)}
                >
                  {filter}
                </button>
              ))}
            </div>
          </Col>

          {isLoadingConversations && (
            <Col xCenter className="pt-7">
              <Loading isLoading />
            </Col>
          )}

          {/* 🟠🟠🟠 CHAT COUNT */}
          {!isLoadingConversations && (
            <Row gap1 className="text-sm text-white/40">
              <span className="tabular-nums">
                {filteredConversations.length}
              </span>
              {filteredConversations.length === 1
                ? t.conversationList.conversation
                : t.conversationList.conversations}
            </Row>
          )}

          {/* 🟠🟠🟠 CONVERSATION LIST */}
          {!isLoadingConversations && (
            <Col className="h-0 grow overflow-y-auto">
              {filteredConversations.map((conversation) => (
                <ConversationCard
                  key={conversation.id}
                  conversation={conversation}
                />
              ))}
            </Col>
          )}
        </Col>
      )}

      <button
        onClick={() => setIsChatListHidden(!isChatListHidden)}
        className={cn(
          'mt-auto flex size-input shrink-0 cursor-pointer items-center justify-center rounded border border-space/20 bg-space/10 transition-colors',
          'hover:border-space/30 hover:bg-space/20',
        )}
      >
        <LucidePanelLeftClose className="size-[20px] text-white" />
      </button>
    </div>
  );
};
